import {
   computeDerivedValues,
   type MeasurementEntry,
   type MetricDefinition,
} from '@fasting-timer/shared'

import { getEntryTimestampMs } from '../../state/measurementsStore'
import type { RangeFilterKey } from '../../utils/timeRange'

export type TimelinePoint = { x: number; y: number; entryId: string }
/** One entry's contribution to a bucket, kept so a bar can be drawn as stacked sessions. */
export type VolumeBucketPart = { value: number; timestamp: number }
export type VolumeBucket = {
   start: number
   end: number
   total: number
   /** Contributions in chronological order (bottom of the bar first). */
   parts: VolumeBucketPart[]
}
export type BucketSize = 'day' | 'week' | 'month'
/** Bucket sizes the effort chart offers; a daily effort total is too noisy to be useful. */
export type EffortBucketSize = Exclude<BucketSize, 'day'>

/** Mean Gregorian month length, used to prorate monthly targets onto shorter buckets. */
const DAYS_PER_MONTH = 365.25 / 12
const DAYS_PER_BUCKET: Record<BucketSize, number> = {
   day: 1,
   week: 7,
   month: DAYS_PER_MONTH,
}

/**
 * Snaps an SI value to the nearest multiple of the metric's grouping step.
 *
 * @param value - The value to snap.
 * @param snapTo - The step size.
 * @returns The snapped value.
 */
function snapToStep(value: number, snapTo: number) {
   return Math.round(value / snapTo) * snapTo
}

/**
 * Lists the distinct snapped group values present in the entries, ascending.
 *
 * @param entries - The entries.
 * @param definition - The metric definition (uses `groupBy`).
 * @returns The group options, or an empty list when the metric has no grouping.
 */
export function getGroupOptions(
   entries: readonly MeasurementEntry[],
   definition: MetricDefinition,
): number[] {
   const groupBy = definition.groupBy
   if (!groupBy) {
      return []
   }
   const values = new Set<number>()
   for (const entry of entries) {
      const raw = entry.values[groupBy.field]
      if (typeof raw === 'number') {
         values.add(snapToStep(raw, groupBy.snapTo))
      }
   }
   return [...values].sort((a, b) => a - b)
}

/**
 * Returns whether an entry belongs to a group (snapped `groupBy` value).
 *
 * @param entry - The entry.
 * @param definition - The metric definition.
 * @param group - The group value, or null for all.
 * @returns True when the entry matches.
 */
function entryMatchesGroup(
   entry: MeasurementEntry,
   definition: MetricDefinition,
   group: number | null,
) {
   const groupBy = definition.groupBy
   if (group === null || !groupBy) {
      return true
   }
   const raw = entry.values[groupBy.field]
   return typeof raw === 'number' && snapToStep(raw, groupBy.snapTo) === group
}

/**
 * Returns a field or derived value of an entry by key.
 *
 * @param entry - The entry.
 * @param definition - The metric definition.
 * @param key - The field or derived key.
 * @returns The value, or null when not computable.
 */
export function getEntryValue(
   entry: MeasurementEntry,
   definition: MetricDefinition,
   key: string,
) {
   if (key in definition.derived) {
      return computeDerivedValues(definition, entry.values)[key] ?? null
   }
   return entry.values[key] ?? null
}

/**
 * Returns the metric's primary value for an entry (what "best" is judged on).
 *
 * @param entry - The entry.
 * @param definition - The metric definition.
 * @returns The primary value, or null when not computable.
 */
export function getPrimaryValue(entry: MeasurementEntry, definition: MetricDefinition) {
   return getEntryValue(entry, definition, definition.primary)
}

/**
 * Filters entries to a time range and (optionally) a group, oldest first.
 *
 * @param entries - The entries.
 * @param definition - The metric definition.
 * @param range - The visible range (epoch ms).
 * @param group - The active group, or null for all.
 * @returns The matching entries in chronological order.
 */
export function selectEntries(
   entries: readonly MeasurementEntry[],
   definition: MetricDefinition,
   range: { rangeStart: number; rangeEnd: number },
   group: number | null,
) {
   return entries
      .filter(entry => {
         const timestamp = getEntryTimestampMs(entry)
         return (
            timestamp >= range.rangeStart &&
            timestamp <= range.rangeEnd &&
            entryMatchesGroup(entry, definition, group)
         )
      })
      .sort((a, b) => getEntryTimestampMs(a) - getEntryTimestampMs(b))
}

/**
 * Builds timeline points (a value over time) from chronological entries.
 *
 * @param entries - The entries.
 * @param definition - The metric definition.
 * @param key - The field or derived key to plot (defaults to the primary value).
 * @returns The points, skipping entries without that value.
 */
export function buildTimelinePoints(
   entries: readonly MeasurementEntry[],
   definition: MetricDefinition,
   key: string = definition.primary,
): TimelinePoint[] {
   return entries.flatMap(entry => {
      const value = getEntryValue(entry, definition, key)
      return value === null ?
            []
         :  [{ x: getEntryTimestampMs(entry), y: value, entryId: entry.entryId }]
   })
}

/**
 * Picks the best entry by primary value, honouring `lowerIsBetter`.
 *
 * @param entries - The entries.
 * @param definition - The metric definition.
 * @returns The best entry, or null when none has a primary value.
 */
export function findBestEntry(
   entries: readonly MeasurementEntry[],
   definition: MetricDefinition,
): MeasurementEntry | null {
   let best: { entry: MeasurementEntry; value: number } | null = null
   for (const entry of entries) {
      const value = getPrimaryValue(entry, definition)
      if (value === null) {
         continue
      }
      const better =
         best === null ||
         (definition.lowerIsBetter ? value < best.value : value > best.value)
      if (better) {
         best = { entry, value }
      }
   }
   return best?.entry ?? null
}

/**
 * Least-squares line through the points.
 *
 * @param points - The points.
 * @returns Slope (per ms) and intercept, or null with fewer than two distinct x values.
 */
export function computeLinearTrend(points: readonly TimelinePoint[]) {
   if (points.length < 2) {
      return null
   }
   const meanX = points.reduce((sum, point) => sum + point.x, 0) / points.length
   const meanY = points.reduce((sum, point) => sum + point.y, 0) / points.length
   let numerator = 0
   let denominator = 0
   for (const point of points) {
      numerator += (point.x - meanX) * (point.y - meanY)
      denominator += (point.x - meanX) ** 2
   }
   if (denominator <= 0) {
      return null
   }
   const slope = numerator / denominator
   return { slope, intercept: meanY - slope * meanX }
}

/**
 * Chooses the bar-chart bucket size for a range filter.
 *
 * @param filterKey - The range filter.
 * @returns The bucket size.
 */
export function getBucketSize(filterKey: RangeFilterKey): BucketSize {
   if (filterKey === 'week') {
      return 'day'
   }
   if (filterKey === 'month' || filterKey === 'quarter') {
      return 'week'
   }
   return 'month'
}

/**
 * Returns the local start of the bucket containing a timestamp (weeks start on Monday).
 *
 * @param timestamp - Epoch ms.
 * @param size - The bucket size.
 * @returns Epoch ms at the bucket start.
 */
export function getBucketStart(timestamp: number, size: BucketSize) {
   const date = new Date(timestamp)
   date.setHours(0, 0, 0, 0)
   if (size === 'week') {
      const weekday = (date.getDay() + 6) % 7
      date.setDate(date.getDate() - weekday)
   } else if (size === 'month') {
      date.setDate(1)
   }
   return date.getTime()
}

/** Most months the monthly effort chart shows, on a wide screen. */
const MAX_EFFORT_MONTHS = 12

/**
 * Chooses how many trailing months the monthly effort chart shows, so bars
 * stay slim on wide charts without getting cramped on phones.
 *
 * @param chartWidth - The chart's rendered width in CSS pixels.
 * @returns The number of months to show.
 */
export function getEffortMonthCount(chartWidth: number) {
   if (chartWidth < 480) {
      return 4
   }
   if (chartWidth < 768) {
      return 6
   }
   return MAX_EFFORT_MONTHS
}

/**
 * Widens a range so it covers at least the months the monthly effort chart
 * can show, ending where the range ends.
 *
 * @param range - The selected range.
 * @returns The range, starting no later than the first of those months.
 */
export function extendRangeToEffortMonths(range: {
   rangeStart: number
   rangeEnd: number
}) {
   const start = new Date(getBucketStart(range.rangeEnd, 'month'))
   start.setMonth(start.getMonth() - (MAX_EFFORT_MONTHS - 1))
   return {
      rangeStart: Math.min(range.rangeStart, start.getTime()),
      rangeEnd: range.rangeEnd,
   }
}

/**
 * Advances a bucket start to the next bucket.
 *
 * @param start - Epoch ms at a bucket start.
 * @param size - The bucket size.
 * @returns Epoch ms at the next bucket start.
 */
function getNextBucketStart(start: number, size: BucketSize) {
   const date = new Date(start)
   if (size === 'day') {
      date.setDate(date.getDate() + 1)
   } else if (size === 'week') {
      date.setDate(date.getDate() + 7)
   } else {
      date.setMonth(date.getMonth() + 1)
   }
   return date.getTime()
}

/**
 * Sums a per-entry value per bucket across the range, including empty buckets so
 * the bar chart shows gaps.
 *
 * @param entries - The entries (any order).
 * @param valueOf - Returns the value to sum for an entry, or null to skip it.
 * @param range - The visible range (epoch ms).
 * @param size - The bucket size.
 * @returns Buckets in chronological order.
 */
export function buildVolumeBuckets(
   entries: readonly MeasurementEntry[],
   valueOf: (entry: MeasurementEntry) => number | null,
   range: { rangeStart: number; rangeEnd: number },
   size: BucketSize,
): VolumeBucket[] {
   const buckets: VolumeBucket[] = []
   let start = getBucketStart(range.rangeStart, size)
   let guard = 0
   while (start <= range.rangeEnd && guard < 400) {
      const end = getNextBucketStart(start, size)
      buckets.push({ start, end, total: 0, parts: [] })
      start = end
      guard += 1
   }
   for (const entry of entries) {
      const timestamp = getEntryTimestampMs(entry)
      const value = valueOf(entry)
      const bucket = buckets.find(item => timestamp >= item.start && timestamp < item.end)
      if (bucket && typeof value === 'number') {
         bucket.total += value
         bucket.parts.push({ value, timestamp })
      }
   }
   return buckets
}

/**
 * Prorates a monthly target onto a bucket size so the target line stays
 * meaningful when the bars are weeks or days.
 *
 * @param monthlyTarget - The target per month, or null when unset.
 * @param size - The bucket size.
 * @returns The target per bucket, or null.
 */
export function prorateMonthlyTarget(monthlyTarget: number | null, size: BucketSize) {
   if (monthlyTarget === null || monthlyTarget <= 0) {
      return null
   }
   return (monthlyTarget * DAYS_PER_BUCKET[size]) / DAYS_PER_MONTH
}
